import { useCallback, useEffect, useRef, useState } from "react";
import { API, formatApiErrorDetail } from "../auth/api";

export function useContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // StrictMode double-invokes effects; without this guard every page load
  // pulled the full list twice, which tripped the bulk-read alerting.
  const inFlight = useRef(null);

  const refetch = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    setLoading(true);
    const request = (async () => {
      try {
        const { data } = await API.get("/contacts");
        setContacts(Array.isArray(data) ? data : []);
        setError(null);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
        inFlight.current = null;
      }
    })();
    inFlight.current = request;
    return request;
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const addContact = useCallback(async (payload) => {
    const { data } = await API.post("/contacts", payload);
    setContacts((prev) => [...prev, data]);
    return data;
  }, []);

  const updateContact = useCallback(async (id, payload) => {
    const { data } = await API.put(`/contacts/${id}`, payload);
    setContacts((prev) => prev.map((c) => (c.id === id ? data : c)));
    return data;
  }, []);

  const deleteContact = useCallback(async (id) => {
    await API.delete(`/contacts/${id}`);
    setContacts((prev) => prev.filter((c) => c.id !== id));
  }, []);

  return { contacts, loading, error, refetch, addContact, updateContact, deleteContact };
}
